import {
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { ethers } from 'ethers';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { validEmailRegex } from '../../../../constants/regexs';
import { CoinGeckoContext } from '../../../../contexts/app-state-context/coingecko/coingecko.context';
import { useCloudfrontNftAsset } from '../../../../hooks/cloudfront-hook';
import { useMarketplaceContract } from '../../../../hooks/marketplace-contract.hooks';
import { useGetNft } from '../../../../hooks/nft-api.hook';
import { useNFTContract } from '../../../../hooks/nft-contract.hooks';
import { useNFTErc721Contract } from '../../../../hooks/nft-erc-721-contract.hooks';
import type { Asset, Listing } from '../../../../lib/graph/MogulMarketplace';
import { AssetType } from '../../../../lib/graph/MogulMarketplace';
import { translateIpfsToPinata } from '../../../../lib/nfts/nft-catalog.service';
import { getNetworkCurrencySymbol } from '../../../../lib/smart-contract-utils';
import {
  formatFloatField,
  formatIntField,
  parseFloatField,
  parseIntField,
} from '../../../../utils/form.util';
import { Button } from '../../../core/button';
import { InputField } from '../../../core/input-field';
import { Panel } from '../../panel/Panel.component';
import styles from './SellAuctionModal.module.css';

export interface SellAuctionModalProps {
  asset: Asset;
  listing: Listing;
}
export interface SellAuction extends SellAuctionModalProps {
  hideModal: () => void;
}

export interface SellNFTFormValues {
  starsPrice?: number;
  ethPrice?: number;
  numListings?: number;
}

const ListNft: React.FC<SellAuction> = ({ asset, hideModal }) => {
  const { starsEthRate } = useContext(CoinGeckoContext);
  const dbNft = useGetNft(asset.tokenAddress, asset.tokenId.toString());

  const { cloudfrontUrl: imageCloudfront } = useCloudfrontNftAsset(
    dbNft?.image,
  );
  const { cloudfrontUrl: animationCloudfront } = useCloudfrontNftAsset(
    dbNft?.animation,
  );
  const image = imageCloudfront ?? translateIpfsToPinata(asset.image ?? '');
  const animation_url =
    animationCloudfront ?? translateIpfsToPinata(asset.animation_url ?? '');

  const [values, setValues] = useState<SellNFTFormValues>({
    starsPrice: 0,
    ethPrice: 0,
    numListings: 1,
  });

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isAuction, setIsAuction] = useState(false);
  const isStarsAuction = true;
  const isStarsType = true;
  const isEthType = false;
  const warning = false;
  const [saleDuration, setSaleDuration] = useState('1');
  const [nftsOwned, setNftsOwned] = useState(0);
  const [numNftsLoaded, setNumNftsLoaded] = useState(false);
  const [approvingMarketplace, setApprovingMarketplace] = useState(false);
  const [approved, setApproved] = useState(false);
  const [showEmailField, setEmailFieldVisibility] = useState(false);
  const [disableEmailField, setDisableEmailField] = useState(true);

  const nftContract = useNFTContract({
    nftContractAddress: asset.tokenAddress as string,
  });
  const nftErc721 = useNFTErc721Contract({
    nftContractAddress: asset.tokenAddress as string,
  });
  const marketplaceContract = useMarketplaceContract({
    type: asset.type ?? AssetType.Erc1155,
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    const setVal = async () => {
      const result = await marketplaceContract.isNotificationsEnabled();
      setNotificationsEnabled(result.email.length > 0);
      marketplaceContract.setNotificationEmail(result.email);
      setDisableEmailField(false);
    };
    void setVal();
  }, []);

  // get number of owned nfts from contract
  useEffect(() => {
    void (async () => {
      if (!numNftsLoaded) {
        try {
          if (asset.type === AssetType.Erc721) {
            // assumed that if this modal is open then they are the owner of nft
            setNftsOwned(1);
          } else {
            const ownedAmount = await nftContract.balanceOf(
              asset.tokenId.toString(),
            );
            setNftsOwned(ownedAmount?.toNumber() ?? 0);
          }
          setNumNftsLoaded(true);
        } catch (e) {
          window.Sentry?.captureException(e, {
            tags: {
              file: 'SellAuctionModal.component.tsx',
              function: 'use effect to get owned number of nfts',
            },
          });
          console.error(e);
        }
      }
    })();
  });

  useEffect(() => {
    if (!nftContract.initialized) {
      return;
    }

    void (async () => {
      const approval = await nftContract.getNftApprovalStatus(
        marketplaceContract.walletDetails.address ?? '',
        marketplaceContract.address,
      );

      setApproved(approval);
    })();
  }, [nftContract.initialized]);

  const selection =
    useRef<{
      start: number | null;
      end: number | null;
      element: HTMLInputElement;
    } | null>(null);

  const input = <T extends keyof SellNFTFormValues>(
    field: T,
    parse: (value: string) => string | undefined,
    format: (value: SellNFTFormValues[T]) => string,
  ) => ({
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = event.currentTarget;

      const value = target.value;
      const parsed = parse(value);

      selection.current = {
        element: target,
        start: target.selectionStart,
        end: target.selectionEnd,
      };

      setValues(v => ({ ...v, [field]: parsed }));
    },
    value: format(values[field]),
  });

  const getStartTimeWithPadding = () => {
    // add 15 mins to account for transaction time
    // also converting to seconds for smart contract
    return parseInt(String(Date.now() / 1000 + 900), 10);
  };

  const getEndTime = (auctionLengthDays: number) => {
    // converting to seconds for smart contract
    return parseInt(String(Date.now() / 1000 + 86400 * auctionLengthDays), 10);
  };

  const approveMarketplaceContract = async () => {
    setApprovingMarketplace(true);

    const currentContract =
      asset.type === AssetType.Erc721 ? nftErc721 : nftContract;

    let result = false;
    try {
      const isAlreadyApproved =
        (await currentContract.isApprovedForAll(
          marketplaceContract.walletDetails.address ?? '',
          marketplaceContract.address,
        )) ?? false;

      result =
        isAlreadyApproved ||
        (await currentContract.setApprovalForAll(
          marketplaceContract.address,
          true,
        ));
    } finally {
      setApprovingMarketplace(false);
    }

    return result;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async event => {
    event.preventDefault();

    if (submitting || !marketplaceContract.initialized) {
      return;
    }

    setSubmitting(true);

    try {
      if (isAuction) {
        const startingPrice = isStarsAuction
          ? ethers.utils.parseEther(
              values.starsPrice !== undefined
                ? values.starsPrice.toString()
                : '0',
            )
          : ethers.utils.parseEther(
              values.ethPrice !== undefined ? values.ethPrice.toString() : '0',
            );
        const listingAmount =
          values.numListings !== undefined ? values.numListings : 1;
        await marketplaceContract
          .createAuction(
            asset.tokenAddress as string,
            asset.tokenId,
            listingAmount,
            startingPrice,
            getStartTimeWithPadding(),
            getEndTime(Number(saleDuration)),
            0, // reserve price
            isStarsAuction,
          )
          .then(() => {
            hideModal();
          })
          .catch(error => {
            setSubmitting(false);
            console.error(error);
          });
      } else {
        const listingAmount =
          values.numListings !== undefined ? values.numListings : 1;
        await marketplaceContract
          .createListing(
            asset.tokenAddress as string,
            asset.tokenId,
            listingAmount,
            ethers.utils.parseEther(
              values.starsPrice !== undefined
                ? values.starsPrice.toString()
                : '0',
            ),
            ethers.utils.parseEther(
              values.ethPrice !== undefined ? values.ethPrice.toString() : '0',
            ),
            isStarsType,
            isEthType,
          )
          .then(() => {
            hideModal();
          })
          .catch(error => {
            setSubmitting(false);
            console.error(error);
          });
      }
    } catch (error) {
      setSubmitting(false);
      throw error;
    }
  };

  return (
    <Panel
      className="overflow-hidden"
      bodyClassName={styles.panel + ' flex flex-col rounded-lg'}
    >
      <div className="flex flex-row">
        <div className={styles.sellNFTInfo + ' flex flex-col'}>
          <div className="flex">
            <div
              className={
                styles.modalSectionLeft +
                ' flex flex-col items-center justify-center pt-6'
              }
            >
              {animation_url ? (
                <video
                  className={styles.image + ' md:w-96 w-56 mx-6'}
                  src={animation_url}
                  autoPlay
                  loop
                  muted
                />
              ) : (
                image && (
                  <img
                    src={image}
                    alt="nft"
                    className={styles.image + ' lg:w-96 w-56'}
                  />
                )
              )}

              <div>
                <div className={styles.title + ' text-center py-4 mx-6 mt-4'}>
                  {asset.title}
                </div>
                <p className={styles.sellerEthAddress + ' text-center pb-6'}>
                  Created by{' '}
                  {(asset.creator?.address as string).slice(0, 9) +
                    '...' +
                    (asset.creator?.address as string).slice(-6)}
                </p>
              </div>
            </div>
            <div
              className={styles.modalSectionRight + ' flex flex-col pl-4 pt-4'}
            >
              <div className={[styles.title, styles.sellNFTTitle].join(' ')}>
                Sell Item
              </div>
              <div className={styles.smallTitleInfo}>
                <div className={styles.title + ' text-center py-4 mx-6 mt-4'}>
                  {asset.title}
                </div>
                <p className={styles.sellerEthAddress + ' text-center pb-6'}>
                  Created by{' '}
                  {(asset.creator?.address as string).slice(0, 9) +
                    '...' +
                    (asset.creator?.address as string).slice(-6)}
                </p>
              </div>
              <form autoComplete="off" onSubmit={handleSubmit} className="mb-6">
                <p className={styles.inputLabel}>Sale Type</p>
                <select
                  name="Sale Type"
                  id="saleType"
                  className={styles.sellNFTSelect}
                  disabled={!marketplaceContract.initialized || submitting}
                  onBlur={event => {
                    setIsAuction(event.target.value === 'auction');
                  }}
                  onChange={event => {
                    setEmailFieldVisibility(false);
                    setIsAuction(event.target.value === 'auction');
                  }}
                >
                  <option value="directListing">Direct Listing</option>
                  <option value="auction">Auction</option>
                </select>
                <div>
                  <p className={`${styles.inputLabel} pt-6`}>
                    Number of Nfts to list
                  </p>
                  <InputField
                    label=""
                    hideLabel
                    InputProps={{
                      disabled: submitting,
                      className: styles.inputField,
                      ...input('numListings', parseIntField, formatIntField),
                    }}
                  />
                </div>
                {isAuction && (
                  <div>
                    <p className={`${styles.inputLabel} pt-2`}>
                      Auction Duration (Will start 15 mins from now)
                    </p>
                    <select
                      name="Sale Duration"
                      id="saleDuration"
                      className={styles.sellNFTSelect}
                      disabled={!marketplaceContract.initialized || submitting}
                      onBlur={event => {
                        setSaleDuration(event.target.value);
                      }}
                    >
                      <option value="1">1 day</option>
                      <option value="3">3 days</option>
                      <option value="7">7 days</option>
                      <option value="14">14 days</option>
                      <option value="31">31 days</option>
                    </select>
                  </div>
                )}
                {isAuction ? (
                  <>
                    <div>
                      <p className={`${styles.inputLabel} pt-6`}>
                        Starting Price (
                        {isStarsAuction ? 'MOGUL' : getNetworkCurrencySymbol()})
                      </p>
                      <InputField
                        label=""
                        hideLabel
                        InputProps={{
                          disabled: submitting,
                          className: styles.inputField,
                          ...input(
                            isStarsAuction ? 'starsPrice' : 'ethPrice',
                            parseFloatField,
                            formatFloatField,
                          ),
                        }}
                      />
                    </div>
                    {notificationsEnabled ? (
                      <p>
                        <Checkbox
                          checked
                          disabled
                          style={{
                            color: '#fcd559',
                          }}
                        />
                        Already Subscribed to Email Notifications
                      </p>
                    ) : (
                      <>
                        <p>
                          <Tooltip
                            title="To be notified about future marketplace transactions, you will be asked to sign a message on metamask before confirming your current bid. The signed message is only used to verify your wallet address"
                            placement="top-start"
                            arrow
                          >
                            <Checkbox
                              disabled={disableEmailField}
                              onChange={() =>
                                setEmailFieldVisibility(!showEmailField)
                              }
                              style={{
                                color: '#fcd559',
                              }}
                            />
                          </Tooltip>
                          Subscribe to Email Notifications
                        </p>
                        {showEmailField ? (
                          <InputField
                            label=""
                            className="text-white"
                            InputProps={{
                              onChange: (
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                setEmail(e.target.value);
                                marketplaceContract.setNotificationEmail(
                                  e.target.value,
                                );
                              },
                            }}
                          />
                        ) : null}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center">
                      <p className={styles.inputLabel}>Price (MOGUL)</p>
                    </div>
                    <InputField
                      label=""
                      id="starsListingPriceInput"
                      hideLabel
                      InputProps={{
                        disabled: submitting || !isStarsType,
                        className: styles.inputField,
                        ...input(
                          'starsPrice',
                          parseFloatField,
                          formatFloatField,
                        ),
                      }}
                      disabled={!isStarsType}
                    />
                    {notificationsEnabled ? (
                      <p>
                        <Checkbox
                          checked
                          disabled
                          style={{
                            color: '#fcd559',
                          }}
                        />
                        Already Subscribed to Email Notifications
                      </p>
                    ) : (
                      <>
                        <p>
                          <Tooltip
                            title="To be notified about future marketplace transactions, you will be asked to sign a message on metamask before confirming your current sell offer. The signed message is only used to verify your wallet address"
                            placement="top-start"
                            arrow
                          >
                            <Checkbox
                              disabled={disableEmailField}
                              onChange={() =>
                                setEmailFieldVisibility(!showEmailField)
                              }
                              style={{
                                color: '#fcd559',
                              }}
                            />
                          </Tooltip>
                          Subscribe to Email Notifications
                        </p>
                        {showEmailField ? (
                          <InputField
                            label=""
                            className="text-white"
                            InputProps={{
                              onChange: (
                                e: React.ChangeEvent<HTMLInputElement>,
                                // eslint-disable-next-line sonarjs/no-identical-functions
                              ) => {
                                setEmail(e.target.value);
                                marketplaceContract.setNotificationEmail(
                                  e.target.value,
                                );
                              },
                            }}
                          />
                        ) : null}
                      </>
                    )}
                  </>
                )}
                {approved ? (
                  <Button
                    type="submit"
                    fullWidth
                    disabled={
                      !marketplaceContract.initialized ||
                      submitting ||
                      (!isAuction && warning) ||
                      nftsOwned < (values.numListings ?? 1) ||
                      (values.numListings ?? 1) < 1 ||
                      (showEmailField && !validEmailRegex.test(email)) ||
                      ((values.starsPrice?.toString() === '0' ||
                        values.starsPrice?.toString() === '') &&
                        (values.ethPrice?.toString() === '0' ||
                          values.ethPrice?.toString() === ''))
                    }
                  >
                    {!submitting ? (
                      isAuction ? (
                        'Create Auction'
                      ) : (
                        'Create Listing'
                      )
                    ) : (
                      <>
                        {isAuction ? 'Creating Auction' : 'Creating Listing'}
                        <CircularProgress
                          className={'placeholder-white::placeholder ml-4'}
                          size={20}
                        />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    type="button"
                    disabled={
                      !marketplaceContract.initialized || approvingMarketplace
                    }
                    onClick={async () => {
                      setApprovingMarketplace(true);
                      const didApprove = await approveMarketplaceContract();
                      setApproved(didApprove);
                      setApprovingMarketplace(false);
                    }}
                  >
                    {!approvingMarketplace ? (
                      'Approve'
                    ) : (
                      <>
                        Approving
                        <CircularProgress
                          className={'placeholder-white::placeholder ml-4'}
                          size={20}
                        />
                      </>
                    )}
                  </Button>
                )}
              </form>
            </div>
          </div>
        </div>
        <div className="ml-4 relative">
          <IconButton
            className={'h-10 w-10 top-6 right-4 z-30'}
            style={{
              background: '#000000',
              border: 'none',
              position: 'absolute',
            }}
            onClick={hideModal}
          >
            <ClearIcon style={{ color: 'white' }} />
          </IconButton>
        </div>
      </div>
    </Panel>
  );
};
export const SellAuctionModal =
  (props: SellAuctionModalProps) =>
  ({ hideModal }: SellAuction) => {
    return <ListNft hideModal={hideModal} {...props} />;
  };
